import { z } from 'zod';

export const TRANSACTION_TYPES = ['expense', 'income', 'transfer', 'refund', 'adjustment'] as const;

export const transactionTypeSchema = z.enum(TRANSACTION_TYPES);
export type TransactionType = z.infer<typeof transactionTypeSchema>;

export const expenseInSchema = z.object({
  date: z.string().date(),
  account: z.string().min(1).max(64),
  credit: z.number().nonnegative().default(0),
  debit: z.number().nonnegative().default(0),
  purpose: z.string().max(200).default(''),
  transactionType: transactionTypeSchema.default('expense'),
  fromTo: z.string().max(200).default(''),
  comments: z.string().max(500).default(''),
});
export type ExpenseIn = z.infer<typeof expenseInSchema>;

export const expenseSchema = expenseInSchema.extend({
  id: z.string().min(1),
  sheetRow: z.number().int().positive(),
});
export type Expense = z.infer<typeof expenseSchema>;

export const expenseCreateSchema = expenseInSchema.refine((v) => v.credit > 0 || v.debit > 0, {
  message: 'Enter either a Credit or a Debit amount.',
});
export type ExpenseCreate = z.infer<typeof expenseCreateSchema>;

// ---- Analytics DTOs -------------------------------------------------
export const monthlyTotalSchema = z.object({
  month: z.string(),
  debit: z.number(),
  credit: z.number(),
  net: z.number(),
});
export type MonthlyTotal = z.infer<typeof monthlyTotalSchema>;

export const purposeBreakdownSchema = z.object({
  purpose: z.string(),
  total: z.number(),
  pct: z.number(),
});
export type PurposeBreakdown = z.infer<typeof purposeBreakdownSchema>;

export const accountBalanceSchema = z.object({
  account: z.string(),
  credit: z.number(),
  debit: z.number(),
  balance: z.number(),
});
export type AccountBalance = z.infer<typeof accountBalanceSchema>;

export const expenseSummarySchema = z.object({
  totalDebit: z.number(),
  totalCredit: z.number(),
  net: z.number(),
  budget: z.number().nullable(),
  monthlyBreakdown: z.array(monthlyTotalSchema),
  purposeBreakdown: z.array(purposeBreakdownSchema),
  accountBalances: z.array(accountBalanceSchema),
  topPurposes: z.array(purposeBreakdownSchema),
});
export type ExpenseSummary = z.infer<typeof expenseSummarySchema>;

// ---- Sources (Accounts tab in the Sheet) ----------------------------
export const sourceSchema = z.object({
  account: z.string(),
  openingFunds: z.number(),
  actualFunds: z.number(),
  ledgerCredit: z.number(),
  ledgerDebit: z.number(),
  computedBalance: z.number(),
  isCash: z.boolean().default(false),
});
export type Source = z.infer<typeof sourceSchema>;

// ---- Sensible default suggestions (chips in the form) ----------------
export const COMMON_ACCOUNTS = ['Bank', 'Credit Card', 'Cash', 'UPI'] as const;
export const COMMON_PURPOSES = [
  'Materials',
  'Labour',
  'Design & Architect',
  'Permits',
  'Equipment',
  'Utilities',
  'Landscaping',
  'Furniture',
  'Misc',
] as const;
